// Een foto uit de telefoon klaarmaken voor een notitie.
//
// Aanleiding: de eerste keer dat er op de NAS een foto in een notitie moest, kwam er niets.
// In de log van de container stonden vijf keer achter elkaar 413's — de foto's waren groter
// dan de acht megabyte die de server aannam, en de app zei er niets over. Een foto van een
// moderne telefoon is dan ook al gauw 8 tot 15 MB en vierduizend pixels breed.
//
// Zo'n origineel in een notitie is ook zonde: het staat in elke nachtelijke back-up en elke
// export, en je ziet er op een scherm niets van terug. Daarom maakt de browser er eerst een
// schermvullende versie van. Lukt dat niet, dan gaat het origineel alsnog de deur uit en
// zegt de server er wel van of het past.

/**
 * Wat de server aanneemt — houd dit gelijk met `GROOTSTE` in `server/kern/bestanden.ts`.
 * We kijken er hier zelf naar omdat een te groot lichaam onderweg wordt afgekapt: de
 * browser ziet dan geen 413 maar een verbroken verbinding, en meldt dus iets anders dan er
 * aan de hand is.
 */
export const GROOTSTE_BESTAND = 20 * 1024 * 1024;

/** Hieronder is verkleinen de moeite niet: een schermafbeelding van een half MB is prima. */
const KLEIN_GENOEG = 1024 * 1024;

/** Ruim voor een scherm met hoge resolutie, en klein genoeg om vlot te laden. */
const GROOTSTE_ZIJDE = 2048;

/** Onder de kwaliteit van 0,82 zie je het aan vlakken met een verloop (een lucht, een muur). */
const KWALITEIT = 0.82;

export type Klaargemaakt = { inhoud: Blob; mime: string; verkleind: boolean };

export async function verkleinAlsNodig(bestand: File): Promise<Klaargemaakt> {
  const zoalsHetIs: Klaargemaakt = { inhoud: bestand, mime: bestand.type, verkleind: false };

  // Een gif verliest zijn animatie zodra we hem opnieuw tekenen; die laten we met rust.
  if (bestand.size <= KLEIN_GENOEG || bestand.type === "image/gif") return zoalsHetIs;

  try {
    // `from-image`: een staande foto draagt zijn draaiing in de exif-gegevens mee. Tekenen we
    // die zonder meer op een doek, dan ligt hij daarna op zijn kant.
    const plaatje = await createImageBitmap(bestand, { imageOrientation: "from-image" });
    const schaal = Math.min(1, GROOTSTE_ZIJDE / Math.max(plaatje.width, plaatje.height));
    const breedte = Math.round(plaatje.width * schaal);
    const hoogte = Math.round(plaatje.height * schaal);

    const doek = document.createElement("canvas");
    doek.width = breedte;
    doek.height = hoogte;
    const stift = doek.getContext("2d");
    if (!stift) return zoalsHetIs;
    stift.drawImage(plaatje, 0, 0, breedte, hoogte);
    plaatje.close();

    // Webp houdt doorzichtigheid heel; jpeg maakt van een doorzichtige achtergrond zwart.
    // Kan de browser geen webp schrijven, dan laten we een png liever ongemoeid.
    const webp = kanWebpSchrijven();
    if (!webp && bestand.type === "image/png") return zoalsHetIs;
    const mime = webp ? "image/webp" : "image/jpeg";

    const kleiner = await new Promise<Blob | null>((klaar) => doek.toBlob(klaar, mime, KWALITEIT));
    // Soms is het origineel al zuiniger (een strakke png met weinig kleuren); dan winnen we niets.
    if (!kleiner || kleiner.size >= bestand.size) return zoalsHetIs;
    return { inhoud: kleiner, mime, verkleind: true };
  } catch {
    // Kan de browser het bestand niet lezen — een heic van een iPhone bijvoorbeeld — dan is
    // het origineel nog altijd beter dan niets.
    return zoalsHetIs;
  }
}

let webpAntwoord: boolean | null = null;

function kanWebpSchrijven(): boolean {
  if (webpAntwoord === null) {
    const doek = document.createElement("canvas");
    doek.width = 1;
    doek.height = 1;
    webpAntwoord = doek.toDataURL("image/webp").startsWith("data:image/webp");
  }
  return webpAntwoord;
}
